/**
 * Images plugin for Lexical - simplified from lexical playground
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $wrapNodeInElement } from "@lexical/utils";
import {
    $createNodeSelection,
    $createParagraphNode,
    $createRangeSelection,
    $getNodeByKey,
    $getSelection,
    $insertNodes,
    $isNodeSelection,
    $isRootOrShadowRoot,
    $setSelection,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    createCommand,
    DRAGOVER_COMMAND,
    DRAGSTART_COMMAND,
    DROP_COMMAND,
    isHTMLElement,
    LexicalCommand,
} from "lexical";
import { useEffect } from "react";
import { mergeRegister } from "@lexical/utils";
import { $createImageNode, $isImageNode, ImageNode } from "../../nodes/ImageNode";

const THUMB_SIZE = 80;

/** Create a thumbnail preview that follows the cursor during drag. Returns [element, offsetX, offsetY]. */
function createDragPreviewImage(
    event: DragEvent,
    src: string,
): { element: HTMLElement; offsetX: number; offsetY: number } {
    const img = event.target as HTMLImageElement;
    if (img?.tagName === "IMG" && img.complete && img.naturalWidth > 0) {
        try {
            const canvas = document.createElement("canvas");
            const scale = Math.min(THUMB_SIZE / img.naturalWidth, THUMB_SIZE / img.naturalHeight, 1);
            canvas.width = Math.round(img.naturalWidth * scale);
            canvas.height = Math.round(img.naturalHeight * scale);
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const wrapper = document.createElement("div");
                wrapper.style.cssText =
                    "position:absolute;left:-9999px;border-radius:4px;box-shadow:0 2px 12px rgba(0,0,0,0.25);background:white;overflow:hidden;pointer-events:none;";
                wrapper.style.width = canvas.width + "px";
                wrapper.style.height = canvas.height + "px";
                wrapper.appendChild(canvas);
                document.body.appendChild(wrapper);
                setTimeout(() => wrapper.remove(), 0);
                const w = canvas.width;
                const h = canvas.height;
                return { element: wrapper, offsetX: w / 2, offsetY: h / 2 };
            }
        } catch {
            // Fall through to img fallback
        }
    }
    const thumb = document.createElement("img");
    thumb.src = src;
    thumb.width = THUMB_SIZE;
    thumb.height = THUMB_SIZE;
    thumb.style.cssText =
        "position:absolute;left:-9999px;object-fit:contain;border-radius:4px;box-shadow:0 2px 12px rgba(0,0,0,0.25);pointer-events:none;";
    document.body.appendChild(thumb);
    setTimeout(() => thumb.remove(), 0);
    return { element: thumb, offsetX: THUMB_SIZE / 2, offsetY: THUMB_SIZE / 2 };
}

export type InsertImagePayload = Readonly<{
    altText: string;
    src: string;
    width?: number | "inherit";
    height?: number | "inherit";
}>;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand("INSERT_IMAGE_COMMAND");

function $getImageNodeInSelection(): ImageNode | null {
    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
        return null;
    }
    const nodes = selection.getNodes();
    const node = nodes[0];
    return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): InsertImagePayload | null {
    const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
    if (!dragData) return null;
    try {
        const { type, data } = JSON.parse(dragData);
        if (type !== "image" || !data?.src) return null;
        return data;
    } catch {
        return null;
    }
}

declare global {
    interface DragEvent {
        rangeOffset?: number;
        rangeParent?: Node;
    }
}

function canDropImage(event: DragEvent): boolean {
    const target = event.target as Node | null;
    if (!target) return false;
    const element = isHTMLElement(target) ? target : target.parentElement;
    if (!element || !isHTMLElement(element)) return false;
    return !element.closest("code, span.editor-image") && !!element.closest("div[class*='ContentEditable__root']");
}

function getDragSelection(event: DragEvent): Range | null | undefined {
    if (typeof document.caretRangeFromPoint === "function") {
        return document.caretRangeFromPoint(event.clientX, event.clientY) ?? undefined;
    }
    if (event.rangeParent) {
        const selection = document.getSelection();
        if (selection) {
            selection.collapse(event.rangeParent, event.rangeOffset || 0);
            return selection.getRangeAt(0);
        }
    }
    return undefined;
}

export default function ImagesPlugin(): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!editor.hasNodes([ImageNode])) {
            throw new Error("ImagesPlugin: ImageNode not registered on editor");
        }

        const $onDragStart = (event: DragEvent): boolean => {
            const node = $getImageNodeInSelection();
            if (!node) return false;
            const dataTransfer = event.dataTransfer;
            if (!dataTransfer) return false;
            dataTransfer.setData("text/plain", "_");
            const { element, offsetX, offsetY } = createDragPreviewImage(event, node.getSrc());
            dataTransfer.setDragImage(element, offsetX, offsetY);
            dataTransfer.setData(
                "application/x-lexical-drag",
                JSON.stringify({
                    type: "image",
                    data: {
                        altText: node.getAltText(),
                        src: node.getSrc(),
                        width: node.__width,
                        height: node.__height,
                    },
                }),
            );
            return true;
        };

        const $onDragover = (event: DragEvent): boolean => {
            if (!getDragImageData(event)) return false;
            if (canDropImage(event)) {
                event.preventDefault();
                event.dataTransfer!.dropEffect = "move";
                return true;
            }
            return false;
        };

        const $onDrop = (event: DragEvent): boolean => {
            const node = $getImageNodeInSelection();
            if (!node) return false;
            const data = getDragImageData(event);
            if (!data) return false;
            event.preventDefault();
            if (!canDropImage(event)) return true;
            const range = getDragSelection(event);
            let insertedImageKey: string | null = null;
            editor.update(
                () => {
                    const currentNode = $getImageNodeInSelection();
                    if (currentNode) {
                        currentNode.remove();
                    }
                    const rangeSelection = $createRangeSelection();
                    if (range != null) {
                        rangeSelection.applyDOMRange(range);
                    }
                    $setSelection(rangeSelection);
                    const imageNode = $createImageNode({
                        altText: data.altText || "",
                        src: data.src,
                        width: data.width,
                        height: data.height,
                    });
                    $insertNodes([imageNode]);
                    if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
                        $wrapNodeInElement(imageNode, $createParagraphNode);
                    }
                    insertedImageKey = imageNode.getKey();
                    const nodeSelection = $createNodeSelection();
                    nodeSelection.add(insertedImageKey);
                    $setSelection(nodeSelection);
                },
                { tag: "drag-drop" },
            );
            // Lexical syncs selection from DOM on drop, overwriting our NodeSelection. Re-apply after the sync and click.
            if (insertedImageKey) {
                setTimeout(() => {
                    editor.update(
                        () => {
                            const node = $getNodeByKey(insertedImageKey!);
                            if ($isImageNode(node)) {
                                const nodeSelection = $createNodeSelection();
                                nodeSelection.add(insertedImageKey!);
                                $setSelection(nodeSelection);
                            }
                        },
                        { tag: "drag-drop-reselect" },
                    );
                    editor.focus();
                }, 0);
            }
            return true;
        };

        return mergeRegister(
            editor.registerCommand<InsertImagePayload>(
                INSERT_IMAGE_COMMAND,
                (payload) => {
                    const imageNode = $createImageNode({
                        altText: payload.altText || "",
                        src: payload.src,
                        width: payload.width,
                        height: payload.height,
                    });
                    $insertNodes([imageNode]);
                    if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
                        $wrapNodeInElement(imageNode, $createParagraphNode);
                        // Select image immediately so user can interact with it right away
                        const nodeSelection = $createNodeSelection();
                        nodeSelection.add(imageNode.getKey());
                        $setSelection(nodeSelection);
                    }
                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            editor.registerCommand(DRAGSTART_COMMAND, $onDragStart, COMMAND_PRIORITY_HIGH),
            editor.registerCommand(DRAGOVER_COMMAND, $onDragover, COMMAND_PRIORITY_LOW),
            editor.registerCommand(DROP_COMMAND, $onDrop, COMMAND_PRIORITY_HIGH),
        );
    }, [editor]);

    return null;
}
